import {
	useSupergridMutations, getLevelByDepth, getLevelDepth, useSuperGridValidate,
} from '@aleph/supergrid';
import React, {
	FC, useEffect, useState,
} from 'react';
import classNames from 'classnames';
import { RedditBreakdowns, RedditLevel, RedditLevelId } from 'wb-reddit/app/routes/superGrid/utils';
import {
	Button,
	ButtonAppearance,
	ButtonColor,
	ButtonSize,
	Dropdown,
	MiddleEllipsisWhisper,
	OpenIcon,
	PlusIcon,
	TrashCanIcon,
	useUrlParams,
} from '@aleph/shared-components';
import RedditMockedPreview from 'wb-reddit/app/routes/superGrid/cellRenderers/tool/mockedPreview';
import {
	REDDIT_DEFAULT_CREATE_VALUES,
	RedditCreateLevels,
	RedditSuperGridMutationsConfig,
} from 'wb-reddit/app/routes/superGrid/mutator/utils';
import { startCase, uniqueId } from 'lodash';
import { getExtraCreativeContent } from 'wb-reddit/app/routes/utils';
import { ErrorBoundary } from '@sentry/react';
import { ICellRendererParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const NameCellRenderer: FC<ICellRendererParams> = (params) => {
	const { t } = useTranslation();
	const [anyAdAccountRowExpanded, setAnyAdAccountRowExpanded] = useState(false);
	const navigate = useNavigate();

	const { setUrlParams, params: urlParams } = useUrlParams({ navigate, useParams });
	const isDashboardView = params.data?.isDashboardView && !params.node.expanded;
	const { context: { setValidations } } = useSuperGridValidate();

	const {
		removeMutationTree,
		addMutation,
		mutations,
	} = useSupergridMutations<RedditSuperGridMutationsConfig>();
	const { breakdown } = params?.context ?? {};

	const removeMutationTreeAndValidations = (hash: string) => {
		removeMutationTree(hash);
		setValidations((curr) => curr.filter((validation) => validation.id !== hash));
	};

	const omitViewToEntity = () => {
		let nextLevel: RedditLevel = RedditLevel.CAMPAIGN;

		if (params.data?.level === RedditLevel.CAMPAIGN) {
			nextLevel = RedditLevel.GROUP;
		}

		if (params.data?.level === RedditLevel.GROUP) {
			nextLevel = RedditLevel.AD;
		}

		setUrlParams({
			...urlParams,
			filters: {
				[RedditLevelId[params.data?.level]]: [{ value: [params.data?.id] }],
			},
			level: nextLevel,
		});
	};

	const addEntity = () => {
		let currLevelDepth = getLevelDepth(params.context.config, params.data?.level);
		let creatingLevel = getLevelByDepth(params.context.config, currLevelDepth);
		let currParent = params.node?.parent?.data ?? params?.data?.parent;

		while (creatingLevel !== RedditLevel.BREAKDOWN) {
			const newMutation = {
				...(REDDIT_DEFAULT_CREATE_VALUES[creatingLevel]?.(currParent) ?? {}),
				name:     `New ${startCase(creatingLevel)} ${(mutations?.[creatingLevel]?.length ?? 0) + 1}`,
				currency: currParent?.currency,
				parent:   currParent,
				hash:     uniqueId('mutation_'),
			};

			addMutation(creatingLevel as RedditCreateLevels, newMutation);

			currParent = newMutation;
			creatingLevel = getLevelByDepth(params.context.config, ++currLevelDepth);
		}
	};

	const pinnedWidth = params.columnApi?.getDisplayedLeftColumns()?.reduce(
		(accu, col) => accu + (col?.getActualWidth() ?? 0),
		0,
	);

	useEffect(
		() => {
			if (params.node?.data?.superGridLevel === RedditLevel.AD_ACCOUNT) {
				let expanded = false;

				params.api.forEachNode(node => {
					if (node.expanded) {
						expanded = true;
					}
				});

				setAnyAdAccountRowExpanded(expanded);
			}
		},
		[params],
	);

	return (
		<div
			className={classNames('w-full px-[8px] h-full max-h-full font-medium', {
				'text-[18px] ': isDashboardView && !anyAdAccountRowExpanded,
			})}
			data-testid="accounts"
		>
			<div
				className={classNames(
					'group flex justify-between gap-[8px] h-full items-center overflow-hidden',
					{
						'text-gray-800': breakdown
							&& breakdown !== RedditBreakdowns.ENTITIES
							&& params.data?.level !== RedditLevel.BREAKDOWN,
					},
				)}
			>
				{
					!params.data?.isCreate
					&& <div
						className="overflow-hidden w-full"
						key={pinnedWidth ?? 'name'}
					>
						<MiddleEllipsisWhisper value={params.value} />
					</div>
				}

				{
					params.data?.isCreate && (
						<Button
							onClick={addEntity}
							leftNode={<PlusIcon fill="black" />}
							appearance={ButtonAppearance.GHOST}
							size={ButtonSize.SMALL}
							color={ButtonColor.GRAY}
							inGrid
							className="hover:!ring-transparent"
						/>
					)
				}

				<div className="group-hover:opacity-100 opacity-0 right-[8px] flex
						gap-[8px] items-center absolute p-[2px] bg-white rounded"
				>
					{
						!params.data?.isCreate
						&& !params.data?.isInCreation
						&& !(params.data?.level === RedditLevel.BREAKDOWN)
						&& params.data?.level !== RedditLevel.AD
						&& !params.data.supergridIsNoDataRow
						&& <Button
							data-testid="open-button"
							title={t('sc: open', 'Open')}
							rightNode={
								<OpenIcon />
							}
							onClick={omitViewToEntity}
							size={ButtonSize.SMALL}
							appearance={ButtonAppearance.OUTLINED}
							inGrid
						/>
					}

					{params.data?.level === RedditLevel.AD && !params.data.supergridIsNoDataRow && (
						<Dropdown
							usePortal
							triggerControl={
								({ openDropdown, closeDropdown, isOpen }) => (
									<Button
										title={t('sc: preview', 'Preview')}
										size={ButtonSize.SMALL}
										appearance={ButtonAppearance.OUTLINED}
										inGrid
										onClick={() => (isOpen ? closeDropdown() : openDropdown())}
									/>
								)}
						>
							{() => (
								<ErrorBoundary
									fallback={<div>{t('sc: preview err', "Couldn't generate preview")}</div>}
								>
									<RedditMockedPreview
										adAccount={params.data?.parent?.parent?.parent?.adAccount}
										creativeAllowComments={params?.data?.creativeAllowComments}
										creativeHeadline={params?.data?.creativeHeadline}
										creativeType={params?.data?.creativeType}
										creativeThumbnailUrl={params?.data?.creativeThumbnailUrl}
										creativeContent={
											getExtraCreativeContent(params)
												.filter(content => !!content.creativeContentMediaUrl)}
									/>
								</ErrorBoundary>
							)}
						</Dropdown>
					)}

					{!params.data?.id // lack of id proves that this entity is in creation and doesn't exist
						&& !params.data?.isCreate // don't show on + rows
						&& <Button
							leftNode={
								<TrashCanIcon />
							}
							onClick={() => removeMutationTreeAndValidations(params.data?.hash)}
							size={ButtonSize.SMALL}
							appearance={ButtonAppearance.OUTLINED}
							color={ButtonColor.RED}
							data-testid="remove-entry-button"
							inGrid
						/>
					}
				</div>
			</div>
		</div>
	);
};

export default NameCellRenderer;
